import { twMerge } from 'tailwind-merge';

import { Text } from '@/components/ui/text';
import { View } from '@/components/ui/view';

/**
 * Which surface the badge is drawn on. Only the fill and its ring change; the
 * form is identical, so the two never read as different badges.
 *
 * Named for the surface rather than its brightness, because brightness is not
 * the stable thing: the theme ramps invert, so a "light" fill is only light in
 * one theme.
 *
 * `card` sits on a themed card and rides the ramp, which inverts with it: a pale
 * fill with dark type in light mode, dark with pale in dark mode, legible either
 * way. The default, and right wherever the badge is the only marker on its
 * surface.
 *
 * There is no loud variant. One was added for this card and then removed once
 * the tint was chosen over it: tertiary orange and both brand mid-tones were
 * tried, and the tint won. Recorded because the variant existing and matching
 * `card` exactly is worse than not existing, and because the next person to
 * find the badge quiet next to a classification badge should know it was a
 * decision rather than an oversight.
 *
 * `brand` sits on the brand field, which is one fixed color in both themes. Its
 * tokens are therefore fixed too, since a ramp token would repaint the badge
 * while the surface under it stayed put.
 */
type NewBadgeSurface = 'card' | 'brand';

const SURFACE_CLASSES: Record<NewBadgeSurface, { container: string; text: string }> = {
  card: { container: 'bg-primary-100', text: 'text-primary-700' },
  brand: { container: 'bg-typography-white', text: 'text-typography-black' },
};

type NewBadgeProps = {
  surface?: NewBadgeSurface;
  className?: string;
};

/**
 * The one eyebrow badge marking something as new, shared by every surface that
 * needs it so the marker cannot fork into several that drift apart.
 *
 * Uppercase micro type rather than a sentence-case word, so it reads as an
 * annotation on whatever it sits beside instead of part of that label. Callers
 * decide *whether* to show it and for how long; this only decides how it looks.
 *
 * Whoever adds the third caller: change it here, not at the call site.
 */
export function NewBadge({ surface = 'card', className }: NewBadgeProps) {
  const surfaceClasses = SURFACE_CLASSES[surface];

  return (
    <View
      className={twMerge('shrink-0 px-2 py-0.5', surfaceClasses.container, className)}>
      <Text
        size="2xs"
        className={twMerge('font-semibold uppercase tracking-wide', surfaceClasses.text)}>
        New
      </Text>
    </View>
  );
}
